import { Form } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  EmptyItem,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useSWR from 'swr';
import { postApi } from '../../apiUtils';
import { Loader } from '../../components/loader/Loader';
import { useAssetClass } from '../../contexts/assetClass';
import { useAuth } from '../../contexts/auth';
import { CONFIG_KEYS, useConfigByKey } from '../../contexts/configContext';
import { useTranslation } from '../../contexts/translation';
import { useIsWorkSheetAllPermission } from '../../hooks/useIsWorkSheetAllPermission';
import { useSafeGoBack } from '../../hooks/useSafeGoBack';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { convertMinsToHrsMins } from '../../pages/Tasks/Columns';
import { Asset } from '../../types/asset.types';
import { CapacityCategory, CapacityCategoryPerson } from '../../types/capacityCategory.types';
import { DocumentType } from '../../types/documentType.types';
import { FaultType } from '../../types/faultType';
import { Task } from '../../types/task.types';
import { TaskStatusApiResponse } from '../../types/TaskStatus';
import { WorkActivity } from '../../types/workActivity.types';
import { WorkActivityGroup } from '../../types/workActivityGroup.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { dateTimeFieldOptionsWithSerializationAndInterval } from '../../utils/dates.types';
import { dxSelectBoxConfig } from '../tasks/TasksForm';
import './WorksheetsForm.scss';

interface UpdateFormData {
  topLevelAssetId?: number;
  assetId?: number;
  taskId?: number;
  capacityCategoryId?: number;
  operatorId?: number;
  customerProjectId?: number;
  activityStarted?: string;
}

const dateInputFormat = 'YYYY-MM-DD HH:mm:ss';

interface Props {
  defaultFormData: UpdateFormData;
}

export const WorksheetsNewForm = ({ defaultFormData }: Props) => {
  const { assetClass } = useAssetClass();
  const { translate } = useTranslation();
  const transFormName = 'WorkSheet.Create';
  const { user: authUser } = useAuth();
  const safeGoBack = useSafeGoBack('/home');
  const [hasPermissionAllToCreateWorkSheet, loadingPermission] = useIsWorkSheetAllPermission();
  const configRestrictWorkActivitiesByCapacityCategoryPerson = useConfigByKey(
    CONFIG_KEYS.RESTRICT_WA_BY_CAPACITY_CATEGORY,
  );
  const restrictWorkActivitiesByCapacityCategoryPerson =
    configRestrictWorkActivitiesByCapacityCategoryPerson
      ? Boolean(
          configRestrictWorkActivitiesByCapacityCategoryPerson.value === 'True' &&
            configRestrictWorkActivitiesByCapacityCategoryPerson.enabled,
        )
      : false;
  const now = moment();

  const formRef = useRef<Form>(null);
  const formData = useRef<UpdateFormData>({
    activityStarted: now.format(dateInputFormat),
    customerProjectId: defaultFormData.customerProjectId,
  });

  //#region States
  const [loading, setLoading] = useState(false);
  const [selectedTopLevelAssetId, setSelectedTopLevelAssetId] = useState<number | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
  const [selectedCapacityCategoryId, setSelectedCapacityCategoryId] = useState<number | null>(null);
  const [selectedWorkActivityGroupId, setSelectedWorkActivityGroupId] = useState<number | null>(
    null,
  );
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);
  const [selectedActivityStarted, setSelectedActivityStarted] = useState<string | null>(
    now.format(dateInputFormat),
  );
  const [selectedActivityFinished, setSelectedActivityFinished] = useState<string | null>(null);
  const [operatorCounter, setOperatorCounter] = useState(0);
  const [loadAllTasks, setLoadAllTasks] = useState(false);
  //#endregion

  const [prompt, setDirty, setClean] = useUnsavedChangesWarning('', () => {
    if (formRef.current!.instance.validate().isValid)
      onSubmit(null).then(() => {
        safeGoBack();
      });
  });

  //#region API calls
  // API seznam top level assets
  const { data: topLevelAssets, isValidating: isValidatingTopLevelAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS_TOP_LEVEL}?classId=${assetClass}&activeOnly=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );

  // API seznam assets
  const { data: assets, isValidating: isValidatingAssets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );

  // API seznam tasks
  const { data: tasks, isValidating: isValidatingTasks } = useSWR<Task[]>(
    loadAllTasks
      ? `${API_URL.TASK_BY_CLASS_WORKSHEET_CREATE_EDIT}?classId=${assetClass}&statusIdArr=10&statusIdArr=20&statusIdArr=50`
      : selectedAssetId
      ? `${API_URL.TASK_BY_CLASS_WORKSHEET_CREATE_EDIT}?classId=${assetClass}&statusIdArr=10&statusIdArr=20&statusIdArr=50&assetId=${selectedAssetId}`
      : null,
    fetcher,
    swrConfig,
  );

  // API - jeden TASK, v případě, že přijde v URL
  const { data: taskFromUrl, isValidating: isValidatingTaskFromUrl } = useSWR<Task>(
    defaultFormData.taskId ? `${API_URL.TASK_ITEM}?id=${defaultFormData.taskId}` : null,
    fetcher,
    swrConfig,
  );

  // API seznam operators
  const { data: operators, isValidating: isValidatingOperators } = useSWR<CapacityCategoryPerson[]>(
    `${API_URL.CAPACITY_CATEGORY_PERSON_LIST}?visibleOnly=true`,
    fetcher,
    swrConfig,
  );

  // API capacity categories
  const { data: capacityCategories, isValidating: isValidatingCapacityCategories } = useSWR<
    CapacityCategory[]
  >(`${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`, fetcher, swrConfig);

  // API work activity groups
  const { data: workActivityGroups, isValidating: isValidatingWorkActivityGroups } = useSWR<
    WorkActivityGroup[]
  >(
    selectedCapacityCategoryId
      ? `${
          API_URL.WORK_ACTIVITY_GROUP_LIST_BY_CAPACITY
        }?capacityCategoryId=${selectedCapacityCategoryId}${
          selectedDocumentTypeId ? `&documentTypeId=${selectedDocumentTypeId}` : ''
        }`
      : null,
    fetcher,
    swrConfig,
  );

  // API work activity
  const { data: workActivities, isValidating: isValidatingWorkActivities } = useSWR<WorkActivity[]>(
    `${API_URL.WORK_ACTIVITY_LIST}`,
    fetcher,
    swrConfig,
  );

  // API document types
  const { data: documentTypes, isValidating: isValidatingDocumentTypes } = useSWR<DocumentType[]>(
    `${API_URL.GET_LIST_FOR_TASK_CREATE}?parentId=${assetClass}&taskCreateWorksheet=true&visibleOnly=true`,
    fetcher,
    swrConfig,
  );

  // API worksheet task time validation
  const {
    data: disableWorksheetTaskTimeValidation,
    isValidating: isValidatingDisableWorksheetTaskTimeValidation,
  } = useSWR<boolean>(
    `${API_URL.GET_CONFIG_IS_ENABLED}?name=disableWorksheetTaskTimeValidation`,
    fetcher,
    swrConfig,
  );

  // API fault types
  const { data: faultTypes, isValidating: isValidatingFaultTypes } = useSWR<FaultType[]>(
    `${API_URL.FAULT_TYPE_LIST}`,
    fetcher,
    swrConfig,
  );

  // API task process config
  const { data: taskProcessConfig, isValidating: isValidatingTaskProcessConfig } =
    useSWR<TaskStatusApiResponse>(
      `${API_URL.TASK_PROCESS_CONFIG_LIST}?visibleOnly=${true}`,
      fetcher,
      swrConfig,
    );
  //#endregion

  //#region UseEffects
  // Při mountu - mohlo se stát, že z URL přišel asset nebo task.
  // Tady se nemůže stát, že přijde zároveň customerProjectId
  useEffect(() => {
    if (!topLevelAssets || !assets || !taskFromUrl) return;
    if (
      isValidatingTopLevelAssets ||
      isValidatingAssets ||
      isValidatingOperators ||
      isValidatingCapacityCategories ||
      isValidatingWorkActivityGroups ||
      isValidatingWorkActivities ||
      isValidatingDocumentTypes ||
      isValidatingDisableWorksheetTaskTimeValidation ||
      isValidatingFaultTypes ||
      isValidatingTaskProcessConfig ||
      loadingPermission ||
      isValidatingTaskFromUrl
    )
      return;

    // Kontrola, jestli již není task nastavený
    if (formData.current.taskId) return;

    if (defaultFormData.assetId) {
      // Pokud přišel asset, nastavíme.
      const asset = assets.find((x) => x.id === defaultFormData.assetId);
      if (asset) {
        formRef.current?.instance.updateData('assetId', defaultFormData.assetId);
        setSelectedAssetId(defaultFormData.assetId);
        // Najdeme parenta a nastavíme.
        const parent = topLevelAssets.find((x) => x.id === asset.topLevelParenId);
        if (parent) {
          formRef.current?.instance.updateData('topLevelAssetId', parent.id);
          setSelectedTopLevelAssetId(parent.id);
        }
      }

      // Podíváme se, jestli v profiltrovaném seznamu tasků (podle assetId) existuje task, který přišel. Pokud ano, vybereme.
      if (defaultFormData.taskId) {
        // const task = tasks.find(
        //   (x) => x.id === defaultFormData.taskId && x.assetId === defaultFormData.assetId,
        // );
        const task = taskFromUrl;
        if (task) {
          formRef.current?.instance.updateData('taskId', task.id);
          if (task.started) {
            formRef.current?.instance.updateData(
              'activityStarted',
              moment(task.started).format(dateInputFormat),
            );
            setSelectedActivityStarted(task.started);
          }
          if (task.finished) {
            formRef.current?.instance.updateData(
              'activityFinished',
              moment(task.finished).format(dateInputFormat),
            );
            setSelectedActivityFinished(task.finished);
          }

          // Pokud má task začátek i konec, spočítáme duration. Jinak se nemůže defautlně stát, že by bylo vyplněné finish.
          if (task.started && task.finished)
            calculateDuration(
              moment(task.started).format(dateInputFormat),
              moment(task.finished).format(dateInputFormat),
            );

          setSelectedDocumentTypeId(task.typeId);
          setSelectedTaskId(task.id);
          handleTaskChange(task.id);

          // Operátor = přihlášený uživatel
          if (authUser && operators) {
            const operator = operators.find((x) => x.personId === authUser.personalNumber);
            if (operator) {
              formData.current.operatorId = operator.personId;
              formRef.current?.instance.updateData('operatorId', operator.personId);
              setSelectedOperatorId(operator.personId);
            }
          }

          // Kapacitní kategorie z tasku — pokud neexistuje ve filtrovaném seznamu, nastaví se první
          if (capacityCategories && authUser) {
            let _capacityCategories = [...capacityCategories];
            if (restrictWorkActivitiesByCapacityCategoryPerson && operators)
              _capacityCategories = _capacityCategories.filter((x) =>
                operators.some(
                  (y) => y.personId === authUser.personalNumber && y.capacityCategoryId === x.id,
                ),
              );
            const categoryToSet =
              (task.capacityCategoryId
                ? _capacityCategories.find((x) => x.id === task.capacityCategoryId)
                : null) ?? _capacityCategories[0];
            if (categoryToSet) {
              formRef.current?.instance.updateData('capacityCategoryId', categoryToSet.id);
              setSelectedCapacityCategoryId(categoryToSet.id);
            }
          }
        }
      }

      // Tím končíme. Pokud tedy přišel asset i task, asset má přenost.
      return;
    }

    // Pokud nepřišel asset, ale task ano, najdeme ho v seznamu všech tasků. Vezmeme z něj asset a z assetu parenta. Vše nastavíme.
    if (defaultFormData.taskId) {
      if (!taskFromUrl) return;

      // const task = tasks.find((x) => x.id === defaultFormData.taskId);
      const task = taskFromUrl;
      if (!task) return;

      const asset = assets.find((x) => x.id === task.assetId);
      if (!asset) return;

      const parent = topLevelAssets.find((x) => x.id === asset.topLevelParenId);
      if (!parent) return;

      formRef.current?.instance.updateData('topLevelAssetId', parent.id);
      formRef.current?.instance.updateData('assetId', asset.id);
      formRef.current?.instance.updateData('taskId', task.id);
      if (task.started) {
        formRef.current?.instance.updateData(
          'activityStarted',
          moment(task.started).format(dateInputFormat),
        );
        setSelectedActivityStarted(task.started);
      }
      if (task.finished) {
        formRef.current?.instance.updateData(
          'activityFinished',
          moment(task.finished).format(dateInputFormat),
        );
        setSelectedActivityFinished(task.finished);
      }

      // Pokud má task začátek i konec, spočítáme duration. Jinak se nemůže defautlně stát, že by bylo vyplněné finish.
      if (task.started && task.finished)
        calculateDuration(
          moment(task.started).format(dateInputFormat),
          moment(task.finished).format(dateInputFormat),
        );

      setSelectedTopLevelAssetId(parent.id);
      setSelectedAssetId(asset.id);
      setSelectedTaskId(task.id);
      setSelectedDocumentTypeId(task.typeId);
      handleTaskChange(task.id);

      // Operátor = přihlášený uživatel
      if (authUser && operators) {
        const operator = operators.find((x) => x.personId === authUser.personalNumber);
        if (operator) {
          formData.current.operatorId = operator.personId;
          formRef.current?.instance.updateData('operatorId', operator.personId);
          setSelectedOperatorId(operator.personId);
        }
      }

      // Kapacitní kategorie z tasku — pokud neexistuje ve filtrovaném seznamu, nastaví se první
      if (capacityCategories && authUser) {
        let _capacityCategories = [...capacityCategories];
        if (restrictWorkActivitiesByCapacityCategoryPerson && operators)
          _capacityCategories = _capacityCategories.filter((x) =>
            operators.some(
              (y) => y.personId === authUser.personalNumber && y.capacityCategoryId === x.id,
            ),
          );
        const categoryToSet =
          (task.capacityCategoryId
            ? _capacityCategories.find((x) => x.id === task.capacityCategoryId)
            : null) ?? _capacityCategories[0];
        if (categoryToSet) {
          formRef.current?.instance.updateData('capacityCategoryId', categoryToSet.id);
          setSelectedCapacityCategoryId(categoryToSet.id);
        }
      }
    }
  }, [
    topLevelAssets,
    assets,
    operators,
    capacityCategories,
    restrictWorkActivitiesByCapacityCategoryPerson,
    authUser,
    taskFromUrl,
    isValidatingTopLevelAssets,
    isValidatingAssets,
    isValidatingOperators,
    isValidatingCapacityCategories,
    isValidatingWorkActivityGroups,
    isValidatingWorkActivities,
    isValidatingDocumentTypes,
    isValidatingDisableWorksheetTaskTimeValidation,
    isValidatingFaultTypes,
    isValidatingTaskProcessConfig,
    loadingPermission,
    isValidatingTaskFromUrl,
  ]);

  // Document type nastavujeme defaultně první. Může se změnit z tasku.
  useEffect(() => {
    if (!documentTypes) return;
    if (documentTypes.length > 0) setSelectedDocumentTypeId(documentTypes[0].id);
  }, [documentTypes]);

  // Pokud existuje pouze jedna skupina činností, defautlně ji vybereme. Stejně pak s činnostmi.
  // To samé uděláme pro činnosti
  // Nutné dělat v useEffect, protože se filtruje na API a v klasickém handleru nemáme po změně kapacitní kategorie aktuální seznamy
  useEffect(() => {
    if (!workActivityGroups || !workActivities) return;

    let activityGroup: WorkActivityGroup | undefined = undefined;
    let activity: WorkActivity | undefined = undefined;

    if (workActivityGroups && workActivityGroups.length === 1)
      activityGroup = workActivityGroups[0];
    if (
      workActivities &&
      activityGroup &&
      workActivities.filter((x) => x.workActivityGroupId === activityGroup!.id).length === 1
    )
      activity = workActivities.filter((x) => x.workActivityGroupId === activityGroup!.id)[0];

    // Nenašla se defaultní skupina, nulujeme skupinu i činnost
    if (!activityGroup) {
      formRef.current?.instance.updateData('workActivityGroupId', null);
      formRef.current?.instance.updateData('workActivityId', null);

      setSelectedWorkActivityGroupId(null);
    } else {
      // Skupina se našla, nastavíme default
      formRef.current?.instance.updateData('workActivityGroupId', activityGroup.id);
      // Pokud se našla i činnost, nastavíme, jinak nulujeme
      formRef.current?.instance.updateData('workActivityId', activity ? activity.id : null);

      setSelectedWorkActivityGroupId(activityGroup.id);
    }
  }, [workActivityGroups, workActivities]);
  //#endregion

  //#region Handlers
  // Při výběru parenta se vynuluje asset a task.
  const handleTopLevelAssetChange = useCallback(
    (topLevelAssetId: number) => {
      setSelectedTopLevelAssetId(topLevelAssetId);

      formRef.current?.instance.updateData('assetId', null);
      formRef.current?.instance.updateData('taskId', null);
      formRef.current?.instance.updateData('activityStarted', now.format(dateInputFormat));
      formRef.current?.instance.updateData('activityFinished', null);
      formRef.current?.instance.updateData('duration', null);

      setSelectedActivityStarted(now.format(dateInputFormat));
      setSelectedActivityFinished(null);
      setSelectedAssetId(null);
      handleTaskChange(null);
    },
    [documentTypes, tasks],
  );

  // Při výběru assetu se najde parent a nastaví se. Zároveň vynulujeme task.
  const handleAssetChange = useCallback(
    (assetId: number) => {
      setSelectedAssetId(assetId);
      setLoadAllTasks(false);

      if (!topLevelAssets || !assets) return;

      const asset = assets.find((x) => x.id === assetId);
      if (!asset) {
        handleTaskChange(null);
        return;
      }

      const parent = topLevelAssets.find((x) => x.id === asset.topLevelParenId);
      if (!parent) return;

      formRef.current?.instance.updateData('topLevelAssetId', parent.id);
      formRef.current?.instance.updateData('taskId', null);
      formRef.current?.instance.updateData('activityStarted', now.format(dateInputFormat));
      formRef.current?.instance.updateData('activityFinished', null);
      formRef.current?.instance.updateData('duration', null);

      setSelectedActivityStarted(now.format(dateInputFormat));
      setSelectedActivityFinished(null);
      handleTaskChange(null);
    },
    [topLevelAssets, assets, documentTypes, tasks],
  );

  // V operators jsou duplicitní záznamy. Pro jednoho operátora může být více kapacitních kategorií
  // Pokud uživatel nemá právo WfWorksheetAll, může založit jen na sebe
  const getOperators = useCallback(() => {
    if (!operators || !authUser) return [];
    let _operators = operators.filter(
      (item, index, self) => index === self.findIndex((t) => t.personId === item.personId),
    );

    if (!hasPermissionAllToCreateWorkSheet)
      _operators = operators.filter((x) => x.personId === authUser.personalNumber);

    return _operators;
  }, [hasPermissionAllToCreateWorkSheet, operators, authUser]);

  // Defaultní operátor = přihlášený uživatel, pokud formulář nemá task v URL
  // Pokud je pro operátora jen jedna kapacitní kategorie, nastaví se automaticky
  // Pokud operátor nemá žádnou kapacitní kategorii, operátor se nenastaví
  useEffect(() => {
    if (defaultFormData.taskId) return;
    if (!authUser || !capacityCategories || !operators) return;

    const operator = getOperators().find((x) => x.personId === authUser.personalNumber);
    if (!operator) return;

    let _capacityCategories = [...capacityCategories];
    if (restrictWorkActivitiesByCapacityCategoryPerson)
      _capacityCategories = _capacityCategories.filter((x) =>
        operators.some((y) => y.personId === operator.personId && y.capacityCategoryId === x.id),
      );

    if (_capacityCategories.length === 0) return;

    formData.current.operatorId = operator.personId;
    formRef.current?.instance.updateData('operatorId', operator.personId);
    setSelectedOperatorId(operator.personId);

    if (_capacityCategories.length === 1) {
      formData.current.capacityCategoryId = _capacityCategories[0].id ?? undefined;
      formRef.current?.instance.updateData('capacityCategoryId', _capacityCategories[0].id);
      setSelectedCapacityCategoryId(_capacityCategories[0].id);
    }
  }, [
    getOperators,
    authUser,
    capacityCategories,
    operators,
    restrictWorkActivitiesByCapacityCategoryPerson,
  ]);

  // Při výběru prvního operátora se smaže výběr kapacitní kategorie. Kvůli profiltrování.
  // Výběr mažeme pouze pokud se kapacitní kategorie filtrují = config
  // Mažeme i work activity group a work activity
  const handleOperatorChange = useCallback(
    (operatorId: number) => {
      if (restrictWorkActivitiesByCapacityCategoryPerson) {
        formRef.current?.instance.updateData('capacityCategoryId', null);
        formRef.current?.instance.updateData('workActivityGroupId', null);
        formRef.current?.instance.updateData('workActivityId', null);

        setSelectedCapacityCategoryId(null);
        setSelectedWorkActivityGroupId(null);
      }
      setSelectedOperatorId(operatorId);
    },
    [restrictWorkActivitiesByCapacityCategoryPerson],
  );

  // Při výběru tasku chceme změnit document type
  // Pokud se task clearne, nastavíme doc type jako první z API
  // Vyplní se activityStarted, activityFinished a duration
  const handleTaskChange = useCallback(
    (taskId: number | null) => {
      if (!documentTypes || !tasks) return;

      ReactDOM.unstable_batchedUpdates(() => {
        let documentTypeId: number | undefined = undefined;
        let activityStarted: string | null = now.format(dateInputFormat);
        let activityFinished: string | null = null;
        if (taskId) {
          const task = tasks.find((x) => x.id === taskId);
          documentTypeId = task?.typeId;
          if (task && task.started) activityStarted = moment(task.started).format(dateInputFormat);
          if (task && task.finished) {
            activityFinished = moment(task.finished).format(dateInputFormat);
            // #678 - V případě, že je finished, tak bych nechal prázdné ActivityStart a Duration... Když to vyplníme celé, tak to omylem uložíš s těmi předvyplněnými hodnotami.
            // Pokud je finished, tak tady vynulujeme started. A duration se tedy automaticky nespočítá v metodě calculateDuration
            activityStarted = null;
          }

          // Pokud není vybrán asset, doplníme ho z tasku (případ načtení všech tasků bez filtru)
          if (task?.assetId && assets && topLevelAssets) {
            const asset = assets.find((x) => x.id === task.assetId);
            if (asset) {
              formRef.current?.instance.updateData('assetId', asset.id);
              setSelectedAssetId(asset.id);
              const parent = topLevelAssets.find((x) => x.id === asset.topLevelParenId);
              if (parent) {
                formRef.current?.instance.updateData('topLevelAssetId', parent.id);
                setSelectedTopLevelAssetId(parent.id);
              }
            }
          }

          // Kapacitní kategorie z tasku — nastaví se jen pokud existuje v aktuálně filtrovaném seznamu
          if (task?.capacityCategoryId && capacityCategories) {
            let _capacityCategories = [...capacityCategories];
            if (restrictWorkActivitiesByCapacityCategoryPerson && selectedOperatorId && operators)
              _capacityCategories = _capacityCategories.filter((x) =>
                operators.some(
                  (y) => y.personId === selectedOperatorId && y.capacityCategoryId === x.id,
                ),
              );
            const capacityCategory = _capacityCategories.find(
              (x) => x.id === task.capacityCategoryId,
            );
            if (capacityCategory) {
              formRef.current?.instance.updateData('capacityCategoryId', capacityCategory.id);
              setSelectedCapacityCategoryId(capacityCategory.id);
            } else if (_capacityCategories.length > 0) {
              formRef.current?.instance.updateData('capacityCategoryId', _capacityCategories[0].id);
              setSelectedCapacityCategoryId(_capacityCategories[0].id);
            }
          }
        } else {
          if (documentTypes.length > 0) documentTypeId = documentTypes[0].id;
        }

        formRef.current?.instance.updateData('activityStarted', activityStarted);
        formRef.current?.instance.updateData('activityFinished', activityFinished);

        calculateDuration(activityStarted, activityFinished);
        setSelectedActivityStarted(activityStarted);
        setSelectedActivityFinished(activityFinished);
        setSelectedTaskId(taskId);
        setSelectedDocumentTypeId(documentTypeId ? documentTypeId : null);
      });
    },
    [
      documentTypes,
      tasks,
      assets,
      topLevelAssets,
      setSelectedDocumentTypeId,
      capacityCategories,
      operators,
      restrictWorkActivitiesByCapacityCategoryPerson,
      selectedOperatorId,
    ],
  );

  // Při změně kapacitní kategorie se refetche API activity groups a logika předvyplňování je v useEffect
  const handleCapacityCategoryChange = useCallback((capacityCategoryId: number | null) => {
    if (!capacityCategoryId) {
      formRef.current?.instance.updateData('workActivityGroupId', null);
      formRef.current?.instance.updateData('workActivityId', null);
      setSelectedWorkActivityGroupId(null);
    }
    setSelectedCapacityCategoryId(capacityCategoryId);
  }, []);

  // Při změně skupiny aktivity promažeme aktivitu
  // Pokud je právě jedna aktivita, vybereme ji
  const handleWorkActivityGroupChange = useCallback(
    (workActivityGroupId: number | null) => {
      let activity: any = null;
      if (
        workActivities &&
        workActivityGroupId &&
        workActivities.filter((x) => x.workActivityGroupId === workActivityGroupId).length === 1
      )
        activity = workActivities.filter((x) => x.workActivityGroupId === workActivityGroupId)[0];

      setSelectedWorkActivityGroupId(workActivityGroupId);
      formRef.current?.instance.updateData('workActivityId', activity ? activity.id : null);
    },
    [workActivities],
  );

  // Při změně začátku aktivity přepočítáme duration
  const handleActivityStartedChange = useCallback(
    (activityStarted: string) => {
      calculateDuration(activityStarted, selectedActivityFinished);
      setSelectedActivityStarted(activityStarted);
    },
    [selectedActivityFinished],
  );

  // Při změně konce aktivity přepočítáme duration
  const handleActivityFinishedChange = useCallback(
    (activityFinished: string) => {
      calculateDuration(selectedActivityStarted, activityFinished);
      setSelectedActivityFinished(activityFinished);
    },
    [selectedActivityStarted],
  );

  // Ručně se změnilo duration, přepočítá se activityFinished, případně activityStarted podle konfigurace
  const handleActivityDurationChange = useCallback(
    (e: any) => {
      if (!e.value) return;

      // Transformace vstupu - přepočet na HH:mm
      let value = e.value.trim().replace(',', '.').replace(':', '.');
      if (value.includes('.')) {
        const valueArr = value.split('.');
        value = convertMinsToHrsMins(Number(valueArr[0]) * 60 + Number(valueArr[1]));
      } else value = convertMinsToHrsMins(value);

      const durationParts = value.split(':');
      const duration = moment.duration({
        hours: Number(durationParts[0]),
        minutes: Number(durationParts[1]),
      });

      // Pokud není vyplněný started ani finished, nic nepočítáme a nedovolíme zadat duration
      if (!selectedActivityStarted && !selectedActivityFinished) {
        e.component.option('value', null);
        return;
      }
      e.component.option('value', value);

      if (selectedActivityStarted) {
        const started = moment(selectedActivityStarted);
        // 24.6.2025 Chování s konfigem - Vezmeme started + duration.
        // Pokud výsledek není v budoucnosti, standartně se dopočítá datum do a datum od zůstane.
        // Pokud je do budoucnosti, tak datum do nastavíme na now a datum od je now - duration.
        if (disableWorksheetTaskTimeValidation) {
          const finished = started.clone().add(duration);
          if (finished.isAfter(now)) {
            formRef.current?.instance.updateData('activityFinished', now.format(dateInputFormat));
            const newStarted = now.clone().subtract(duration);
            formRef.current?.instance.updateData(
              'activityStarted',
              newStarted.format(dateInputFormat),
            );

            setSelectedActivityStarted(newStarted.format(dateInputFormat));
            setSelectedActivityFinished(now.format(dateInputFormat));
          } else {
            formRef.current?.instance.updateData(
              'activityFinished',
              finished.format(dateInputFormat),
            );
            setSelectedActivityFinished(finished.format(dateInputFormat));
          }
        }
        // Standardní chování - datum do je started + duration
        else {
          const finished = started.clone().add(duration);
          formRef.current?.instance.updateData(
            'activityFinished',
            finished.format(dateInputFormat),
          );
          setSelectedActivityFinished(finished.format(dateInputFormat));
        }
      }
      // Není vyplněno started, ale je vyplněno finished → dopočítáme started = finished - duration
      else {
        const finished = moment(selectedActivityFinished);
        const newStarted = finished.clone().subtract(duration);
        formRef.current?.instance.updateData('activityStarted', newStarted.format(dateInputFormat));
        setSelectedActivityStarted(newStarted.format(dateInputFormat));
      }
    },
    [disableWorksheetTaskTimeValidation, selectedActivityStarted, selectedActivityFinished],
  );
  //#endregion

  //#region Helpers function
  // Tasky filtrujeme podle assetu (přes API) nebo načítáme všechny (loadAllTasks).
  // Pokud přišel state customerProjectId, filtrujeme i podle něj
  const getTasks = useCallback(() => {
    if (!tasks) return [];
    let _tasks: Task[] = tasks;

    if (defaultFormData.customerProjectId)
      _tasks = _tasks.filter((x) => x.customerProjectId === defaultFormData.customerProjectId);

    return _tasks;
  }, [tasks]);

  // Z konfigu chodí boolean, jestli chceme všechny kategorie nebo jen ty, které jsou u operátorů
  const capacityCategoriesFiltered = useMemo(() => {
    if (!capacityCategories || !operators) return [];
    let _capacityCategories = capacityCategories;

    if (restrictWorkActivitiesByCapacityCategoryPerson)
      _capacityCategories = _capacityCategories.filter(
        (x) =>
          operators.filter(
            (y) => y.personId === selectedOperatorId && y.capacityCategoryId === x.id,
          ).length > 0,
      );

    return _capacityCategories;
  }, [
    capacityCategories,
    operators,
    restrictWorkActivitiesByCapacityCategoryPerson,
    selectedOperatorId,
  ]);

  // Podle activityStrted a activityFinished spočítáme duration
  const calculateDuration = useCallback(
    (activityStarted: string | null, activityFinished: string | null) => {
      // Pro výpočet potřebujeme obě hodnoty, jinak nulujeme
      if (!activityStarted || !activityFinished) {
        formRef.current?.instance.updateData('activityDuration', null);
        return;
      }

      const start = moment(activityStarted);
      const finish = moment(activityFinished);

      // Pokud je start větší než finish, nulujeme
      if (start.isAfter(finish)) {
        formRef.current?.instance.updateData('activityDuration', null);
        return;
      }

      const duration = moment.duration(finish.diff(start));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const durationStr = `${hours < 10 ? '0' + hours : hours}:${
        minutes < 10 ? '0' + minutes : minutes
      }`;

      formRef.current?.instance.updateData('activityDuration', durationStr);
    },
    [selectedActivityStarted, selectedActivityFinished],
  );

  // Validace vstupu duration. Je možné zadat HH:mm nebo HH.mm nebo HH,mm
  const isDurationValid = (value: any): boolean => {
    if (value === undefined || value === null || value === '') return true;
    const normalized = String(value).replace(',', '.').replace(':', '.');
    return !Number.isNaN(Number(normalized));
  };

  // Fault type se zobrazuje pouze pokud má zvolený document type taskShowFaultType = true
  // Pokud se skryje, vynulujeme hdonotu
  const isFaultTypeVisible = useCallback(() => {
    if (!documentTypes || !selectedDocumentTypeId) {
      formRef.current?.instance.updateData('faultTypeId', null);
      return false;
    }

    const documentType = documentTypes.find((dt) => dt.id === selectedDocumentTypeId);
    if (!documentType) {
      formRef.current?.instance.updateData('faultTypeId', null);
      return false;
    }

    return documentType.taskShowFaultType;
  }, [documentTypes, selectedDocumentTypeId]);

  // Logika, jestli má být vidět taksComplete checkbox a jestli má být defaultně checknutý
  const processTaskComplete = useCallback(() => {
    // console.log('------ PROCESS TaskComplete ------');
    // Pokud není vyplněn Activity Finished výkazu, nikdy nechceme dát možnost ukončit výkaz. Tzn. není vidět.
    if (!selectedActivityFinished) {
      // console.log('Není vybrán Activity Finished = skryjeme.');
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // Activity Finished je vyplněno a není vybraný task, checkbox zobrazíme.
    if (!selectedTaskId) {
      // console.log('Není task = zobrazíme.');
      // Kontrolujeme worksheetTaskCompleteBehavior na document type
      // a) worksheetTaskCompleteBehavior = 0, tak se nastaví checkbox na false.
      // b) worksheetTaskCompleteBehavior = 2, tak se nastaví checkbox na true.
      // c) Pokud neplatí nic z předchozího a není vybrán task, chceme defaultně checknout;
      // d) Pokud neplatí nic z předchozího a task je vybrán. Checkneme checknout pouze pokud se zodpovědná osoba tasku rovná operátorovi výkazu. -> Nemůže nastat v této větvi, protože není task.
      if (documentTypes && selectedDocumentTypeId) {
        const documentType = documentTypes.find((x) => x.id === selectedDocumentTypeId);
        // console.log('documentType', documentType);
        if (documentType && documentType.worksheetTaskCompleteBehavior === 0) {
          // console.log(
          //   `Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 0.`,
          // );
          // console.log('taskComplete = false');
          formRef.current?.instance.updateData('taskComplete', false);
        } else if (documentType && documentType.worksheetTaskCompleteBehavior === 2) {
          // console.log(
          //   `Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 1.`,
          // );
          // console.log('taskComplete = true');
          formRef.current?.instance.updateData('taskComplete', true);
        } else {
          // console.log(
          //   `Vybraný document type ${selectedDocumentTypeId} nemá specifikovaný worksheetTaskCompleteBehavior ${documentType?.worksheetTaskCompleteBehavior} a není vybrán task.`,
          // );
          // console.log('taskComplete = true');
          formRef.current?.instance.updateData('taskComplete', true);
        }
      } else {
        // console.log(`Není vybraný document type nebo se nenačetly = nemůžeme zjistit behavior.`);
        // console.log('taskComplete = false');
        formRef.current?.instance.updateData('taskComplete', false);
      }
      return true;
    }

    // Activity Finished je vyplněno a je vybraný task. Podíváme se, jestli je možné task ukončit tzn. existuje actionId = 14. Pokud ano, zobrazíme checkbox.
    // Pokud by náhodou neexistoval document type nebo taskProcessConfig, skryjeme checkbox.
    if (!documentTypes || !taskProcessConfig || !tasks) {
      // console.log('Nejsou načteny documen types nebo process config nebo tasky = skryjeme.');
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // Kontrola jestli je vybraný document type
    if (!selectedDocumentTypeId) {
      // console.log('Není vybraný document type = skryjeme.');
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // Načteme document type
    const documentType = documentTypes.find((x) => x.id === selectedDocumentTypeId);
    if (!documentType) {
      // console.log(`Nepovedlo se nalézt document type ${selectedDocumentTypeId} = skryjeme.`);
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // Načteme task
    const task = tasks.find((x) => x.id === selectedTaskId);
    if (!task) {
      // console.log(`Nepovedlo se nalézt task ${selectedTaskId} = skryjeme.`);
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // Kontrola v task process configu
    // console.log(
    //   `Hledáme v task process configu pro documentTypeId ${documentType.id}, statusFromId ${task.statusId} a actionId 14.`,
    // );
    const taskConfig = taskProcessConfig.find(
      (x) =>
        x.documentTypeId === documentType.id &&
        x.statusFromId === task.statusId &&
        x.actionId === 14,
    );
    if (!taskConfig) {
      // console.log(`Nepovedlo se nalézt v process configu = skryjeme.`);
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
      return false;
    }

    // console.log(`Povedlo se nalézt v process configu = zobrazíme.`);
    // Kontrolujeme taskDocumentTypeCompleteBehavior na document type
    // a) worksheetTaskCompleteBehavior = 0, tak se nastaví checkbox na false.
    // b) worksheetTaskCompleteBehavior = 2, tak se nastaví checkbox na true.
    // c) Pokud neplatí nic z předchozího a není vybrán task, chceme defaultně checknout. -> Nemůže nastat v této větvi, protože musí být task.
    // d) Pokud neplatí nic z předchozího a task je vybrán. Checkneme checknout pouze pokud se zodpovědná osoba tasku rovná operátorovi výkazu.
    if (documentTypes && selectedDocumentTypeId) {
      const documentType = documentTypes.find((x) => x.id === selectedDocumentTypeId);
      if (documentType && documentType.worksheetTaskCompleteBehavior === 0) {
        // console.log(
        //   `Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 0.`,
        // );
        // console.log('taskComplete = false');
        formRef.current?.instance.updateData('taskComplete', false);
      } else if (documentType && documentType.worksheetTaskCompleteBehavior === 2) {
        // console.log(
        //   `Vybraný document type ${selectedDocumentTypeId} má worksheetTaskCompleteBehavior = 1.`,
        // );
        // console.log('taskComplete = true');
        formRef.current?.instance.updateData('taskComplete', true);
      } else if (selectedOperatorId && task.responsiblePersonId === selectedOperatorId) {
        // console.log(
        //   `Vybraný document type ${selectedDocumentTypeId} nemá specifikovaný worksheetTaskCompleteBehavior ${documentType?.worksheetTaskCompleteBehavior}. Task je vybrán, kontroluej se, jestli vybraný operator ${selectedOperatorId} se rovná zodpovědné osobě na tasku ${task.responsiblePersonId}.`,
        // );
        // console.log('taskComplete = true');
        formRef.current?.instance.updateData('taskComplete', true);
      }
    } else {
      // console.log(`Není vybraný document type nebo se nenačetly = nemůžeme zjistit behavior.`);
      // console.log('taskComplete = false');
      formRef.current?.instance.updateData('taskComplete', false);
    }
    return true;
  }, [
    selectedActivityFinished,
    selectedTaskId,
    selectedDocumentTypeId,
    taskProcessConfig,
    tasks,
    documentTypes,
  ]);

  // Resetuje formulář do původního stavu při vstupu
  const handleReset = () => {
    // 1. Aktualizujeme hodnoty v paměti (v useRef objektu)
    formData.current = {
      activityStarted: now.format(dateInputFormat),
      customerProjectId: defaultFormData.customerProjectId,
    };

    // 2. Získáme instanci DevExtreme formuláře
    const formInstance = formRef.current?.instance;
    if (formInstance) {
      // 3. Vnutíme formuláři nová data
      formInstance.option('formData', formData.current);

      // 4. Pomocé states nutné také resetovar
      setSelectedTopLevelAssetId(null);
      setSelectedAssetId(null);
      setSelectedTaskId(null);
      setSelectedOperatorId(null);
      setSelectedCapacityCategoryId(null);
      setSelectedWorkActivityGroupId(null);
      setSelectedDocumentTypeId(
        documentTypes && documentTypes.length > 0 ? documentTypes[0].id : null,
      );
      setSelectedActivityStarted(now.format(dateInputFormat));
      setSelectedActivityFinished(null);
      setOperatorCounter(0);
    }
  };
  //#endregion

  //#region Submit
  const onSubmit = useCallback(
    async (e: any) => {
      if (e) e.preventDefault();

      setLoading(true);

      // Kontrola, jestli je skutečně vybrán documentType DocumentType. Kontroluje i BE, ale máme i zde.
      if (!selectedDocumentTypeId) {
        notify(translate!(`Document type is not selected.`, transFormName), 'error');
        setLoading(false);
        return;
      }

      // Pokud není zvolen task, nabídneme confirm, jestli chce uživatel task založit. Pokud ne, nezkládáme ani worksheet.
      if (!selectedTaskId) {
        const confirmResult = await confirm(
          translate!(
            'No task is selected for the worksheet. Create a new one automatically?',
            'WorkSheet.Create',
          ),
          translate!('Confirm', ''),
        );
        if (!confirmResult) {
          setLoading(false);
          return;
        }
      }

      // Pokud není zvolen task, zakládáme task a následně worksheet
      if (!selectedTaskId) {
        const taskIdCreated = await createWorksheetWithTaskPostAPI();
        if (!taskIdCreated) {
          alert('Chyba při vytváření tasku.');
          setLoading(false);
          return;
        }

        if (operatorCounter > 0)
          await createWorksheetPostAPI(taskIdCreated, formData.current.operatorId);
      }
      // Task je zvolen, zakládáme jen worksheet
      else await createWorksheetPostAPI(selectedTaskId);

      setClean();
      handleReset();
      setLoading(false);
    },
    [selectedDocumentTypeId, selectedTaskId, operatorCounter, assets],
  );

  // Zákládá task a následně worksheet
  const createWorksheetWithTaskPostAPI = useCallback(async () => {
    // TaskName se vyplní podle assetu z formuláře.
    const asset = assets!.find((x) => x.id === formData.current.assetId);
    let taskId: number | null = null;
    await postApi({
      url: `${API_URL.WORKSHEET_POST_WITH_TASK}?autoFinishOlder=true&assetId=${
        formData.current.assetId
      }&typeId=${selectedDocumentTypeId}&capacityCategoryId=${formData.current.capacityCategoryId}
                &priority=${20}&taskName=${asset!.name}`,
      data: { ...formData.current, taskId: 0 },
      callAfterSuccess: async (data) => {
        taskId = data.taskId;
      },
    });

    return taskId;
  }, [selectedDocumentTypeId, assets, operatorCounter]);

  // Zákládá worksheet bez tasku - pro každého operátora
  const createWorksheetPostAPI = useCallback(
    async (taskId: number, submitedOperatorId?: number) => {
      let submitedOperatorIds: any = submitedOperatorId ? [submitedOperatorId] : [];
      for (let index = 0; index <= operatorCounter; index++) {
        const operatorId = formData.current[`operatorId${index === 0 ? '' : '_' + index}`];
        if (operatorId && !submitedOperatorIds.includes(operatorId)) {
          await postApi({
            url: `${API_URL.WORKSHEET_POST2}?autoFinishOlder=true`,
            data: {
              ...formData.current,
              taskId: taskId,
              operatorId: operatorId,
            },
          });
          submitedOperatorIds.push(operatorId);
        }
      }
    },
    [operatorCounter, selectedTaskId],
  );

  //#endregion

  if (
    isValidatingTopLevelAssets ||
    isValidatingAssets ||
    isValidatingTasks ||
    isValidatingOperators ||
    isValidatingCapacityCategories ||
    isValidatingWorkActivityGroups ||
    isValidatingWorkActivities ||
    isValidatingDocumentTypes ||
    isValidatingDisableWorksheetTaskTimeValidation ||
    isValidatingFaultTypes ||
    isValidatingTaskProcessConfig ||
    loadingPermission
  )
    return <Loader />;

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form
          colCount={3}
          formData={formData.current}
          showValidationSummary={true}
          disabled={loading}
          ref={formRef}
          onFieldDataChanged={(e) => {
            setDirty();
          }}
        >
          <SimpleItem
            dataField={'topLevelAssetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: topLevelAssets,
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleTopLevelAssetChange(e.value);
              },
            }}
          >
            <Label text={translate!('Line', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'assetId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: !selectedTopLevelAssetId
                ? assets
                : assets?.filter((x) => x.topLevelParenId === selectedTopLevelAssetId),
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleAssetChange(e.value);
              },
            }}
          >
            <Label text={translate!('Asset', transFormName)} />
            <RequiredRule message={translate!('Select asset', transFormName)} />
          </SimpleItem>
          <SimpleItem
            dataField={'taskId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: function (data) {
                return data && data.customId + ' - ' + data.taskName;
              },
              dataSource: getTasks(),
              buttons: [
                {
                  name: 'loadAll',
                  location: 'before',
                  options: {
                    icon: 'refresh',
                    stylingMode: 'text',
                    hint: translate!('Load tasks without asset filter', transFormName),
                    type: 'danger',
                    onClick: () => setLoadAllTasks(true),
                  },
                },
                'clear',
                'dropDown',
              ],
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleTaskChange(e.value);
              },
            }}
          >
            <Label text={translate!('Task name', transFormName)} />
          </SimpleItem>

          <GroupItem
            cssClass={`worksheet-operators-group${
              !hasPermissionAllToCreateWorkSheet ? ' no-border' : ''
            }`}
          >
            <SimpleItem
              dataField={'operatorId'}
              editorType={'dxSelectBox'}
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'personId',
                displayExpr: 'personName',
                dataSource: getOperators(),
                onValueChanged: (e: any) => {
                  if (!e.event) return;
                  handleOperatorChange(e.value);
                },
              }}
            >
              <Label text={translate!('Operator', transFormName)} />
              <RequiredRule message={translate!('Operator required', transFormName)} />
            </SimpleItem>
            {[...Array(operatorCounter)].map((_, index) => (
              <SimpleItem
                key={index}
                dataField={`operatorId_${index + 1}`}
                editorType="dxSelectBox"
                editorOptions={{
                  ...dxSelectBoxConfig,
                  valueExpr: 'personId',
                  displayExpr: 'personName',
                  dataSource: getOperators(),
                }}
              >
                <Label text={`${translate!(`Operator`, transFormName)} ${index + 1}`} />
              </SimpleItem>
            ))}

            {hasPermissionAllToCreateWorkSheet && (
              <ButtonItem horizontalAlignment={'left'}>
                <ButtonOptions
                  width={'30px'}
                  type={'default'}
                  icon={'add'}
                  stylingMode={'text'}
                  disabled={loading}
                  onClick={() => {
                    setOperatorCounter(operatorCounter + 1);
                  }}
                />
              </ButtonItem>
            )}
          </GroupItem>
          <EmptyItem />
          <EmptyItem />

          <SimpleItem
            dataField={'capacityCategoryId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: capacityCategoriesFiltered,
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleCapacityCategoryChange(e.value);
              },
            }}
          >
            <Label text={translate!('Capacity Category Name', transFormName)} />
            <RequiredRule message={translate!('Select capacity category', transFormName)} />
          </SimpleItem>

          <SimpleItem
            dataField={'workActivityGroupId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              readOnly: isValidatingWorkActivityGroups,
              dataSource: selectedCapacityCategoryId ? workActivityGroups : [],
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleWorkActivityGroupChange(e.value);
              },
            }}
          >
            <Label text={translate!('Activity group', transFormName)} />
            <RequiredRule message={translate!('Activity group required', transFormName)} />
          </SimpleItem>

          <SimpleItem
            dataField={'workActivityId'}
            editorType={'dxSelectBox'}
            editorOptions={{
              ...dxSelectBoxConfig,
              valueExpr: 'id',
              displayExpr: 'name',
              dataSource: selectedWorkActivityGroupId
                ? workActivities?.filter(
                    (x) => x.workActivityGroupId === selectedWorkActivityGroupId,
                  )
                : [],
            }}
          >
            <Label text={translate!('Activity', transFormName)} />
            <RequiredRule message={translate!('Activity required', transFormName)} />
          </SimpleItem>

          <SimpleItem
            dataField="activityStarted"
            editorType="dxDateBox"
            editorOptions={{
              ...dateTimeFieldOptionsWithSerializationAndInterval,
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleActivityStartedChange(e.value);
              },
              disabledDates: (args) => {
                // Configem lze vypnout kontrolu
                if (disableWorksheetTaskTimeValidation) return false;
                // Pokud není task, nekontorlujeme
                if (!selectedTaskId) return false;
                // Nedovolíme jít do minulosti od min(TaskCreate, Task.Started)
                const task = tasks?.find((x) => x.id === selectedTaskId);
                if (!task) return false;

                let dateToCompare: string = task.created.toString();
                if (task.started && moment(task.started).isBefore(moment(task.created)))
                  dateToCompare = task.started;

                return moment(args.date).isBefore(moment(dateToCompare).startOf('day'));
              },
            }}
          >
            <Label text={translate!('Activity started', transFormName)} />
            <RequiredRule message={translate!('Activity started required', transFormName)} />
            <CustomRule
              validationCallback={(e) => {
                // Configem lze vypnout kontrolu
                if (disableWorksheetTaskTimeValidation) return true;
                // Pokud není task, nekontorlujeme
                if (!selectedTaskId) return true;
                // Nedovolíme jít do minulosti od min(TaskCreate, Task.Started)
                const task = tasks?.find((x) => x.id === selectedTaskId);
                if (!task) return true;

                let dateToCompare: string = task.created.toString();
                if (task.started && moment(task.started).isBefore(moment(task.created)))
                  dateToCompare = task.started;

                return moment(e.value).endOf('minute').isSameOrAfter(moment(dateToCompare));
              }}
              type={'custom'}
              message={translate!(
                'Date is invalid, must be greater then now or greater than task create date',
                transFormName,
              )}
              ignoreEmptyValue
            />
          </SimpleItem>
          <SimpleItem
            dataField="activityFinished"
            editorType="dxDateBox"
            editorOptions={{
              ...dateTimeFieldOptionsWithSerializationAndInterval,
              onValueChanged: (e: any) => {
                if (!e.event) return;
                handleActivityFinishedChange(e.value);
              },
              disabledDates: (args) => {
                // Nedovolíme do budoucnosti a nesmí být před activityStarted
                return (
                  moment(args.date).isAfter(now.clone().endOf('minute')) ||
                  (selectedActivityStarted &&
                    moment(args.date)
                      .endOf('day')
                      .isBefore(moment(selectedActivityStarted).startOf('day')))
                );
              },
            }}
          >
            <Label text={translate!('Activity finished', transFormName)} />
            <CustomRule
              validationCallback={(e) => {
                // Nedovolíme do budoucnosti a nesmí být před activityStarted
                return (
                  moment(e.value).startOf('minute').isSameOrBefore(now) &&
                  (!selectedActivityStarted ||
                    moment(e.value).isSameOrAfter(moment(selectedActivityStarted)))
                );
              }}
              type={'custom'}
              message={translate!(
                'Date is invalid, must be lower then now and can not be before activity start',
                transFormName,
              )}
              ignoreEmptyValue
            />
          </SimpleItem>
          <SimpleItem
            dataField="activityDuration"
            editorType="dxTextBox"
            editorOptions={{
              onValueChanged: (e: any) => {
                if (e.event && isDurationValid(e.value)) handleActivityDurationChange(e);
              },
            }}
          >
            <Label text={translate!('Duration', transFormName)} />
            <CustomRule validationCallback={(e) => isDurationValid(e.value)} />
          </SimpleItem>

          {isFaultTypeVisible() && (
            <SimpleItem
              dataField="faultTypeId"
              editorType="dxSelectBox"
              editorOptions={{
                ...dxSelectBoxConfig,
                valueExpr: 'id',
                displayExpr: 'name',
                items: faultTypes,
              }}
            >
              <Label text={translate!('Fault type', transFormName)} />
            </SimpleItem>
          )}

          {isFaultTypeVisible() && <EmptyItem />}
          {isFaultTypeVisible() && <EmptyItem />}

          <SimpleItem
            colSpan={3}
            dataField="description"
            editorType="dxTextArea"
            editorOptions={{
              height: 100,
            }}
          >
            <Label text={translate!('Description', transFormName)} />
          </SimpleItem>

          {processTaskComplete() && (
            <SimpleItem colSpan={3} dataField="taskComplete" editorType="dxCheckBox">
              <Label text={translate!('Task complete', transFormName)} />
            </SimpleItem>
          )}

          <GroupItem colCount={2} colSpan={3} cssClass={'desktop-save-edit-buttons'}>
            <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
              <ButtonOptions
                width={'100%'}
                text={translate!('Close', 'Asset.Update')}
                type={'normal'}
                useSubmitBehavior={false}
                onClick={() => safeGoBack()}
                disabled={loading}
              />
            </ButtonItem>
            <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
              <ButtonOptions
                width={'100%'}
                text={translate!('Save', transFormName)}
                type={'success'}
                useSubmitBehavior={false}
                onClick={() => {
                  if (formRef.current?.instance.validate().isValid) onSubmit(null);
                }}
                disabled={loading}
              />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
      {prompt}
    </>
  );
};
